/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback, useEffect} from 'react';
import {makeStyles, Tooltip} from '@material-ui/core';
import {useSignals} from '@preact/signals-react/runtime';
import {translate} from '@ion/core/src/locale/locale';
import Switch from '@ion/dls/src/components/switch/Switch';
import TopNavHelpIcon from '@ion/dls/src/icons/TopNavHelpIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {notifyOnCreatedJob, setNotifyOnCreatedJob} from './signals/createJobNotifications';
import {getWatchCreatedJobsDefault} from './WatchedJobs';

const useStyles = makeStyles(() => ({
  panel : {
    display         : 'flex',
    justifyContent  : 'space-between',
    backgroundColor : dlsColor('white'),
    padding         : 10,
    alignItems      : 'center'
  },
  title : {
    color : dlsColor('grey1')
  },
  label : {
    display : 'flex'
  },
  tooltip : {
    display    : 'flex',
    alignItems : 'center',
    marginLeft : '6px'
  }
}));

const useTooltipStyles = makeStyles(() => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'tooltip')
  }
}));

/**
 * @CreateJobNotificationsToggle
 *
 * Whether the job about to be created is watched. Sits at the bottom of the
 * Create Job dialog's first step, beside the Account Name chooser, and is
 * styled as the matching row in My Profile - Preferences.
 *
 * Per-job and nothing more: it is seeded from the stored default on every open
 * and never writes it. `CreateJobNotificationsBanner` is the surface which
 * writes the default, and the two agree through `notifyOnCreatedJob` rather
 * than through shared component state, since they render in different places.
 *
 * The signal is also how the answer outlives the dialog: `addJob` hides the
 * editor in the same tick it dispatches the response, so `useAutoWatchCreatedJob`
 * reads the signal once the job id lands rather than asking this component.
 *
 * ```typescript
 *
 *   import CreateJobNotificationsToggle from '.../CreateJobNotificationsToggle';
 *
 *   <CreateJobEditor jobNotificationsToggle={<CreateJobNotificationsToggle />} />
 *
 * ```
 */
export const CreateJobNotificationsToggle = () => {
  useSignals();
  const classes        = useStyles();
  const tooltipClasses = useTooltipStyles();

  useEffect(() => {
    setNotifyOnCreatedJob(getWatchCreatedJobsDefault());
  }, []);

  const onChange = useCallback(({target: {checked}}: ChangeEvent<HTMLInputElement>) => {
    setNotifyOnCreatedJob(checked);
  }, []);

  return (
    <div className={classes.panel}>
      <div className={classes.label}>
        <BodyTypography className={classes.title}>
          {translate('jobsearchengine:createJob.notify')}
        </BodyTypography>
        <Tooltip arrow
          classes={tooltipClasses}
          title={translate('jobsearchengine:createJob.notifyTooltip')}>
          <div className={classes.tooltip}>
            <TopNavHelpIcon/>
          </div>
        </Tooltip>
      </div>
      <Switch disabled={false}
        onChange={onChange}
        checked={notifyOnCreatedJob.value} />
    </div>
  );
};

export default React.memo(CreateJobNotificationsToggle);
